import { toast } from "sonner";
import axios, { AxiosError, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  User, 
  Regimen, 
  WorkoutLog, 
  Achievement,
  ApiError
} from '@/types/api';

// API base URL
const API_BASE_URL = 'https://api.coachshare.online';

// Request deduplication system
const pendingRequests = new Map<string, Promise<any>>();
const recentResponses = new Map<string, { data: any; timestamp: number }>();

const dedupRequest = async <T>(endpoint: string, requestFn: () => Promise<T>): Promise<T> => {
  // Check if we already have a recent response (within 5 seconds)
  const cacheKey = endpoint;
  const cachedResponse = recentResponses.get(cacheKey);
  const now = Date.now();
  
  if (cachedResponse && (now - cachedResponse.timestamp < 5000)) {
    console.log(`Using cached response for ${endpoint} (${((now - cachedResponse.timestamp)/1000).toFixed(1)}s old)`);
    return cachedResponse.data;
  }
  
  // Check if this exact request is already in progress
  if (pendingRequests.has(cacheKey)) {
    console.log(`Request to ${endpoint} already in progress, waiting for result`);
    return pendingRequests.get(cacheKey) as Promise<T>;
  }
  
  // Create a new request promise
  const requestPromise = (async () => {
    try {
      const result = await requestFn();
      // Cache the result
      recentResponses.set(cacheKey, { 
        data: result, 
        timestamp: Date.now() 
      });
      return result;
    } finally {
      // Remove from pending requests when done
      pendingRequests.delete(cacheKey);
    }
  })();
  
  // Store the promise
  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Helper function to safely extract ID from user object
const extractUserId = (user: User | null): string => {
  if (!user) return '';
  if (typeof user._id === 'string') return user._id;
  if (typeof user.id === 'string') return user.id;
  return '';
};

// Generic error handler
const handleError = (error: unknown): never => {
  console.error('API Error:', error);
  if (error instanceof AxiosError && error.response) {
    console.error('Error response:', error.response.data);
  }
  throw error;
};

// Type-safe fetch wrapper with caching ability
export async function fetchApi<T>(
  endpoint: string, 
  options: RequestInit = {},
  errorMessage: string = "An error occurred while fetching data"
): Promise<T> {
  try {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Get authentication token
    const token = localStorage.getItem('token');
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
      credentials: 'include', // Always include credentials
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    return handleError(error);
  }
}

// Create an axios instance for API calls
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add interceptor to include authentication token
api.interceptors.request.use(config => {
  console.log('🚀 API Request:', {
    url: config.url,
    baseURL: config.baseURL,
    method: config.method,
    headers: config.headers,
    data: config.data,
    fullUrl: `${config.baseURL}${config.url}`
  });
  
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, error => {
  console.error('❌ Request interceptor error:', error);
  return Promise.reject(error);
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  response => {
    // Log successful responses
    console.log('✅ API Response:', {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: response.data,
      config: {
        url: response.config.url,
        baseURL: response.config.baseURL,
        method: response.config.method,
        fullUrl: `${response.config.baseURL}${response.config.url}`
      }
    });
    return response;
  },
  error => {
    console.error('❌ API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
        fullUrl: error.config ? `${error.config.baseURL}${error.config.url}` : 'unknown'
      },
      headers: error.config?.headers
    });
    
    if (error instanceof AxiosError && error.response) {
      console.error('Error response:', error.response.data);
      
      // Handle specific error cases
      if (error.response.status === 401) {
        // Clear token and redirect to login
        localStorage.removeItem('token');
        window.location.href = '/auth/login';
        toast.error('Your session has expired. Please log in again.');
      } else if (error.response.status === 0) {
        toast.error('Network error: Unable to connect to the server');
      }
    }
    return Promise.reject(error);
  }
);

// Add workout logs cache
const workoutLogsCache = {
  data: null as WorkoutLog[] | null,
  timestamp: 0,
  ttl: 60000 // 1 minute cache TTL
};

// Helper function to make authenticated API calls with deduplication
const authenticatedApiCall = async <T>(endpoint: string, apiCallFn: () => Promise<T>): Promise<T> => {
  return dedupRequest(endpoint, async () => {
    // Check authentication status once and cache it
    const authStatus = await dedupRequest('/api/auth/check', async () => {
      try {
        const authCheck = await api.get<ApiResponse<{ authenticated: boolean }>>('/api/auth/check');
        return authCheck?.data?.data?.authenticated === true;
      } catch (error) {
        console.error('Auth check failed:', error);
        return false;
      }
    });
    
    if (!authStatus) {
      console.error('User is not authenticated');
      toast.error('Please log in to access this feature');
      return [] as unknown as T;
    }
    
    // User is authenticated, proceed with the API call
    return apiCallFn();
  });
};

// User Service
export const userService = {
  getUsersByIds: async (ids: string[]): Promise<User[]> => {
    if (!ids || ids.length === 0) {
      console.log('[getUsersByIds] No IDs provided, returning empty array');
      return [];
    }
    const idsQuery = ids.join(',');
    console.log('[getUsersByIds] Making request with IDs:', idsQuery);
    console.log('[getUsersByIds] Full request URL:', `${API_BASE_URL}/users/batch?ids=${idsQuery}`);
    
    try {
      console.log('[getUsersByIds] Sending request...');
      const response = await api.get<ApiResponse<{ users: User[] }>>(`/users/batch?ids=${idsQuery}`);
      console.log('[getUsersByIds] Response received:', response.data);
      
      if (response.data && response.data.status === 'success') {
        console.log('[getUsersByIds] Successfully fetched users:', response.data.data.users);
        return response.data.data.users || [];
      }
      console.log('[getUsersByIds] No users found in response');
      return [];
    } catch (error) {
      console.error('[getUsersByIds] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        status: error instanceof AxiosError ? error.response?.status : 'No status',
        data: error instanceof AxiosError ? error.response?.data : 'No data',
        config: error instanceof AxiosError ? error.config : 'No config'
      });
      toast.error("Failed to fetch coach details.");
      return [];
    }
  }
};

// Regimen Service
export const regimenService = {
  getRawRegimens: async (): Promise<Regimen[]> => {
    try {
      const response = await api.get<ApiResponse<Regimen[]>>('/api/regimens');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching raw regimens:', error);
      toast.error('Failed to fetch regimens. Please check if the server is running.');
      return [];
    }
  },
  
  testServerConnection: async (): Promise<boolean> => {
    try {
      const response = await api.get('/api/health');
      return response?.status === 200;
    } catch (error) {
      console.error('Server connection test failed:', error);
      return false;
    }
  },

  getAllRegimens: async (): Promise<Regimen[]> => {
    try {
      const response = await api.get<ApiResponse<{ regimens: Regimen[] }>>('/api/regimens/coach'); 
      const data = response.data?.data?.regimens;
      
      if (Array.isArray(data)) {
        console.log(`Fetched ${data.length} regimens for list`);
        return data;
      } else {
        console.warn('[getAllRegimens] Expected an array in response.data.data.regimens, but received:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching all regimens:', error);
      const errorMessage = error instanceof AxiosError ? error.response?.data?.message : 'Failed to fetch regimens.';
      toast.error(errorMessage);
      return [];
    }
  },

  getAthleteRegimens: async (): Promise<Regimen[]> => {
    try {
      const response = await api.get<ApiResponse<{ regimens: Regimen[] }>>('/api/regimens/athlete');
      const data = response.data?.data?.regimens;
      
      if (Array.isArray(data)) {
        console.log(`Fetched ${data.length} regimens for athlete`);
        return data;
      } else {
        console.warn('[getAthleteRegimens] Expected an array in response.data.data.regimens, but received:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching athlete regimens:', error);
      toast.error('Failed to fetch athlete regimens. Please check if the server is running.');
      return [];
    }
  },

  getRegimenById: async (id: string): Promise<Regimen | null> => {
    try {
      console.log(`Fetching regimen ${id}`);
      const response = await api.get<ApiResponse<{ regimen: Regimen }>>(`/api/regimens/${id}`);

      if (response.data && response.data.status === 'success' && response.data.data?.regimen) {
        const regimenData = response.data.data.regimen;

        if (!regimenData.id && regimenData._id) {
          console.warn("Regimen data from API missing 'id', adding it from '_id'.");
          regimenData.id = regimenData._id.toString();
        }

        if (!regimenData.id) {
          console.error("Critical Error: Regimen data still missing 'id' field after check:", regimenData);
          toast.error('Received invalid regimen data from server.');
          return null;
        }

        console.log("getRegimenById returning:", regimenData);
        return regimenData;
      } else {
        console.warn(`Unexpected response structure for getRegimenById ${id}:`, response.data);
        toast.error('Failed to retrieve regimen details.');
        return null;
      }
    } catch (error) {
      console.error(`Error fetching regimen ${id}:`, error);
      if (error instanceof AxiosError && error.response?.status === 404) {
        toast.error('Regimen not found.');
      } else {
        toast.error('Failed to fetch regimen details. Please try again.');
      }
      return null;
    }
  },

  createRegimen: async (regimenData: Partial<Regimen>): Promise<Regimen> => {
    try {
      console.log('Creating regimen with data:', { 
        name: regimenData.name,
        category: regimenData.category
      });
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        toast.error('Please log in to create a regimen');
        throw new Error('Authentication token not available');
      }
      
      const response = await api.post<ApiResponse<Regimen>>('/api/regimens', regimenData);
      const createdRegimen = response.data.data;
      
      console.log('Regimen created successfully:', { 
        id: createdRegimen._id || createdRegimen.id,
        name: createdRegimen.name
      });
      
      return createdRegimen;
    } catch (error) {
      console.error('Error creating regimen:', error);
      
      if (error instanceof AxiosError && error.response) {
        console.error('Server responded with error:', error.response.status);
        console.error('Error data:', error.response.data);
        
        if (error.response.status === 401) {
          toast.error('You must be logged in to create a regimen');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to create regimens');
        } else if (error.response.status === 400) {
          toast.error('Invalid regimen data. Please check all required fields.');
        } else {
          toast.error('Failed to create regimen. Please try again.');
        }
      } else if (error instanceof AxiosError && error.request) {
        console.error('No response received from server');
        toast.error('Server not responding. Please check your connection.');
      } else {
        console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
        toast.error('An error occurred. Please try again.');
      }
      
      throw error;
    }
  },

  updateRegimen: async (id: string, regimenData: Partial<Regimen>): Promise<Regimen> => {
    try {
      console.log(`Updating regimen ${id} with data:`, { 
        name: regimenData.name,
        category: regimenData.category
      });
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        toast.error('Please log in to update this regimen');
        throw new Error('Authentication token not available');
      }
      
      const response = await api.patch<ApiResponse<Regimen>>(`/api/regimens/${id}`, regimenData);
      const updatedRegimen = response.data.data;
      
      console.log('Regimen updated successfully:', {
        id: updatedRegimen._id || updatedRegimen.id,
        name: updatedRegimen.name
      });
      
      return updatedRegimen;
    } catch (error) {
      console.error(`Error updating regimen ${id}:`, error);
      
      if (error instanceof AxiosError && error.response) {
        console.error('Server responded with error:', error.response.status);
        console.error('Error data:', error.response.data);
        
        if (error.response.status === 401) {
          toast.error('You must be logged in to update this regimen');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to update this regimen');
        } else if (error.response.status === 404) {
          toast.error('Regimen not found. It may have been deleted.');
        } else if (error.response.status === 400) {
          toast.error('Invalid regimen data. Please check all required fields.');
        } else {
          toast.error('Failed to update regimen. Please try again.');
        }
      } else if (error instanceof AxiosError && error.request) {
        console.error('No response received from server');
        toast.error('Server not responding. Please check your connection.');
      } else {
        console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
        toast.error('An error occurred. Please try again.');
      }
      
      throw error;
    }
  },

  deleteRegimen: async (id: string): Promise<void> => {
    try {
      console.log(`Deleting regimen ${id}`);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        toast.error('Please log in to delete this regimen');
        throw new Error('Authentication token not available');
      }
      
      try {
        console.log(`First deleting all workout logs for regimen ${id} to maintain data separation`);
        const logsResponse = await api.delete(`/api/workout-logs/regimen/${id}`);
        console.log(`Successfully deleted associated workout logs, response:`, logsResponse.status);
        toast.success('Successfully removed all workout logs for this program');
      } catch (logsError) {
        console.error(`Error deleting workout logs for regimen ${id}:`, logsError);
        toast.error('Failed to delete associated workout logs, but will continue with regimen deletion');
      }
      
      await api.delete(`/api/regimens/${id}`);
      
      console.log(`Regimen ${id} deleted successfully`);
      
      try {
        await api.delete(`/api/workout-logs/regimen/${id}`);
        console.log('Performed additional cleanup of any remaining workout logs');
      } catch (finalError) {
        console.warn('Additional cleanup attempt failed, but regimen was deleted successfully');
      }
      
      toast.success('Regimen and all associated workout logs deleted successfully');
    } catch (error) {
      console.error(`Error deleting regimen ${id}:`, error);
      
      if (error instanceof AxiosError && error.response) {
        console.error('Server responded with error:', error.response.status);
        console.error('Error data:', error.response.data);
        
        if (error.response.status === 401) {
          toast.error('You must be logged in to delete this regimen');
        } else if (error.response.status === 403) {
          toast.error('You do not have permission to delete this regimen');
        } else if (error.response.status === 404) {
          toast.error('Regimen not found. It may have already been deleted.');
          
          try {
            await api.delete(`/api/workout-logs/regimen/${id}`);
            toast.success('Regimen and associated workout logs deleted successfully');
          } catch (logsError) {
            // Ignore
          }
          
          return;
        } else {
          toast.error('Failed to delete regimen. Please try again.');
        }
      } else if (error instanceof AxiosError && error.request) {
        console.error('No response received from server');
        toast.error('Server not responding. Please check your connection.');
      } else {
        console.error('Error message:', error instanceof Error ? error.message : 'Unknown error');
        toast.error('An error occurred. Please try again.');
      }
      
      throw error;
    }
  },
  
  deleteRegimenLogs: async (regimenId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await api.delete(`/workout-logs/regimen/${regimenId}`);
      console.log(`Successfully deleted workout logs for regimen ${regimenId}`);
      return { success: true };
    } catch (error) {
      console.error(`Error deleting workout logs for regimen ${regimenId}:`, error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  getCoachScheduleRegimens: async (): Promise<Regimen[]> => {
    try {
      const response = await api.get<ApiResponse<{ regimens: Regimen[] }>>('/api/regimens/coach');
      const data = response.data?.data?.regimens;
      
      if (Array.isArray(data)) {
        console.log(`Fetched ${data.length} regimens for schedule`);
        return data;
      } else {
        console.warn('Expected an array in response.data.data.regimens, but received:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching coach schedule regimens:', error);
      const errorMessage = error instanceof AxiosError ? error.response?.data?.message : 'Failed to fetch schedule data.';
      toast.error(errorMessage);
      return [];
    }
  },

  getCoachWorkoutLogs: async (filter: string = 'myAthletes'): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<WorkoutLog[]>>('/api/workout-logs/coach/my-athletes');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching coach workout logs:', error);
      return [];
    }
  },

  assignRegimenBulk: async (regimenId: string, athleteIds: string[]) => {
    const response = await api.post('/api/regimens/assign-bulk', {
      regimenId,
      athleteIds
    });
    return response.data;
  },
};

// Authentication service
export const authService = {
  getCurrentUser: async (): Promise<User | null> => {
    try {
      const userResponse = await api.get<ApiResponse<{ user: User }>>('/auth/me');
      return userResponse.data.data.user;
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  },

  forgotPassword: async (data: { email: string }) => {
    console.log('🔐 [forgotPassword] Starting password reset request:', {
      email: data.email,
      endpoint: '/auth/forgot-password',
      baseURL: API_BASE_URL
    });

    try {
      const response = await api.post('/auth/forgot-password', data);
      console.log('✅ [forgotPassword] Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [forgotPassword] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  },

  resetPassword: async (token: string, password: string) => {
    console.log('🔐 [resetPassword] Starting password reset:', {
      token: token.substring(0, 10) + '...',
      endpoint: `/auth/reset-password/${token}`
    });

    try {
      const response = await api.patch(`/auth/reset-password/${token}`, { password });
      console.log('✅ [resetPassword] Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [resetPassword] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  },
};

// Workout Logs Service
export const workoutLogsService = {
  getCoachWorkoutLogs: async (filter: string = 'myAthletes'): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<WorkoutLog[]>>('/api/workout-logs/coach/my-athletes');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching coach workout logs:', error);
      return [];
    }
  },

  getAthleteWorkoutLogs: async (athleteId: string): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<WorkoutLog[]>>(`/api/workout-logs/coach/athlete/${athleteId}`);
      return response?.data?.data || [];
    } catch (error) {
      console.error(`Error fetching workout logs for athlete ${athleteId}:`, error);
      toast.error('Failed to load athlete logs');
      return [];
    }
  },

  getWorkoutLogStats: async (): Promise<{ hasData: boolean; [key: string]: any }> => {
    try {
      const response = await api.get<ApiResponse<any>>('/api/workout-logs/coach/stats');
      if (response.data && response.data.status === 'success' && response.data.data) {
        return response.data.data;
      } else {
        console.warn('Invalid stats response:', response.data);
        return { hasData: false };
      }
    } catch (error) {
      console.error('Error fetching workout stats:', error);
      return { hasData: false };
    }
  },

  createWorkoutLog: async (logData: Partial<WorkoutLog>): Promise<WorkoutLog> => {
    try {
      const response = await api.post<ApiResponse<WorkoutLog>>('/api/workout-logs', logData);
      return response.data.data;
    } catch (error) {
      console.error('Error creating workout log:', error);
      toast.error('Failed to save workout log');
      throw error;
    }
  },

  getMyWorkoutLogs: async (): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<WorkoutLog[]>>('/api/workout-logs/my-logs');
      if (response.data && response.data.status === 'success') {
        const logs = response.data.data || [];
        logs.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
        return logs;
      } else {
        console.warn('Invalid logs response:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching athlete workout logs:', error);
      toast.error("Could not load your workout history.");
      return [];
    }
  },

  cleanupOrphanedLogs: async (): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.delete<ApiResponse<{ message: string; deletedCount: number }>>('/api/workout-logs/cleanup', {
        data: {} // Send empty body to match backend expectation
      });
      
      if (response.status === 200 && response.data?.status === 'success') {
        const deletedCount = response.data.data?.deletedCount || 0;
        const message = response.data.data?.message || `Successfully deleted ${deletedCount} orphaned logs`;
        
        // If no logs were deleted, show a friendly message
        if (deletedCount === 0) {
          return { 
            success: true, 
            message: 'No orphaned workout logs found to clean up. Your database is already clean!' 
          };
        }
        
        return { 
          success: true, 
          message 
        };
      } else {
        return { 
          success: false, 
          message: response.data?.data?.message || 'Cleanup failed' 
        };
      }
    } catch (error) {
      console.error('Cleanup error:', error);
      const errorMessage = error instanceof AxiosError 
        ? error.response?.data?.message || error.message
        : 'Failed to run cleanup';
      return { success: false, message: errorMessage };
    }
  }
};

// Achievement Service
export const achievementService = {
  getMyAchievements: async (): Promise<Achievement[]> => {
    try {
      const response = await api.get<ApiResponse<{ achievements: Achievement[] }>>('/achievements/my-achievements');
      return response.data.data.achievements;
    } catch (error) {
      console.error('Error fetching achievements:', error);
      toast.error('Failed to load achievements.');
      throw error;
    }
  },
};

// Workout Log Service
export const workoutLogService = {
  getMyLogs: async (): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<{ logs: WorkoutLog[] }>>('/api/workout-logs/my-logs');
      return response.data.data.logs || [];
    } catch (error) {
      console.error('Error fetching workout logs:', error);
      toast.error('Failed to fetch workout logs');
      return [];
    }
  },

  submitWorkoutLog: async (logData: any): Promise<any> => {
    try {
      const response = await api.post<ApiResponse<any>>('/api/workout-logs', logData);
      return response.data;
    } catch (error) {
      console.error('Error submitting workout log:', error);
      throw error;
    }
  },

  getCoachAthleteLogs: async (athleteId: string): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<{ logs: WorkoutLog[] }>>(`/api/workout-logs/coach/athlete/${athleteId}`);
      return response.data.data.logs || [];
    } catch (error) {
      console.error('Error fetching athlete workout logs:', error);
      toast.error('Failed to fetch athlete workout logs');
      return [];
    }
  },

  getCoachAthletesLogs: async (): Promise<WorkoutLog[]> => {
    try {
      const response = await api.get<ApiResponse<{ logs: WorkoutLog[] }>>('/api/workout-logs/coach/my-athletes');
      return response.data.data.logs || [];
    } catch (error) {
      console.error('Error fetching coach athletes workout logs:', error);
      toast.error('Failed to fetch athletes workout logs');
      return [];
    }
  }
};

// Auth check endpoint
export const checkAuth = async (): Promise<ApiResponse<{ authenticated: boolean; user?: User }>> => {
  try {
    const response = await api.get('/api/auth/check');
    return response.data;
  } catch (error) {
    console.error('Auth check error:', error);
    return {
      status: 'error',
      message: 'Authentication check failed',
      data: { authenticated: false }
    };
  }
};

// Helper function to get avatar URL
export const getAvatarUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `https://api.coachshare.online/uploads/avatars/${path}`;
};

export default api; 